import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { _get, _post, _postFile } from "../../Services/baseApi";
import {
  OTA_CAMPAIGN_ACTIVE_URL,
  OTA_CAMPAIGN_LIST_URL,
  OTA_DEPLOY_URL,
  OTA_EQUIPMENT_TARGETS_URL,
  OTA_FIRMWARE_LIST_URL,
  OTA_FIRMWARE_UPLOAD_URL,
  OTA_SESSION_LIST_URL,
  OTA_SUBMIT_URL,
  OTA_TRIGGER_URL,
  otaCampaignRetryUrl,
  otaCampaignLogUrl,
  otaClearRetainedUrl,
  otaResetRetryUrl,
} from "../../Services/endpoints";
import { errorHandler, successToast } from "../../Utils/toast";
import {
  adjustCampaignCounts,
  applyLivePayloadToDevice,
  buildDeviceIndexMap,
  buildLiveLogEvent,
  campaignIdsMatch,
  KNOWN_LIVE_STATUSES,
  shouldAppendLiveEvent,
} from "../../Pages/DeviceOta/otaLogUtils";

const TERMINAL_STATUSES = new Set([
  "success",
  "ota_error",
  "version_mismatch",
  "ota_failed_permanently",
  "failed",
  "cancelled",
]);

const INITIAL_STATE = {
  firmwareList: [],
  sessions: [],
  sessionsTotal: 0,
  campaigns: [],
  campaignsTotal: 0,
  activeCampaign: null,
  campaignLog: null,
  isLoadingLog: false,
  isLoadingMoreLog: false,
  isLoading: false,
  isTriggering: false,
  isUploading: false,
  isSelectingAll: false,
  isRetrying: false,
  liveByMqttId: {},
};

export const getOtaEquipmentTargetsThunk = createAsyncThunk(
  "ota/equipmentTargets",
  async (params, { rejectWithValue }) => {
    try {
      const response = await _get(OTA_EQUIPMENT_TARGETS_URL, {
        searchText: params.searchText || undefined,
      });
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const getFirmwareListThunk = createAsyncThunk(
  "ota/firmwareList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await _get(OTA_FIRMWARE_LIST_URL);
      return response?.data?.result ?? [];
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const uploadFirmwareThunk = createAsyncThunk(
  "ota/uploadFirmware",
  async ({ file, version, notes }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("version", String(version));
      if (notes) formData.append("notes", notes);
      const response = await _postFile(OTA_FIRMWARE_UPLOAD_URL, formData);
      successToast(response?.data?.message || "Firmware uploaded");
      dispatch(getFirmwareListThunk());
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const getOtaCampaignsThunk = createAsyncThunk(
  "ota/campaignList",
  async (params, { rejectWithValue }) => {
    try {
      const response = await _get(OTA_CAMPAIGN_LIST_URL, params);
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const getActiveCampaignThunk = createAsyncThunk(
  "ota/campaignActive",
  async (_, { rejectWithValue }) => {
    try {
      const response = await _get(OTA_CAMPAIGN_ACTIVE_URL);
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const deployOtaBatchThunk = createAsyncThunk(
  "ota/deployBatch",
  async ({ version, targets }, { dispatch, rejectWithValue }) => {
    try {
      const response = await _post(OTA_DEPLOY_URL, {
        version,
        targets: JSON.stringify(targets),
      });
      successToast(response?.data?.message || "OTA deployed");
      dispatch(getActiveCampaignThunk());
      dispatch(getOtaCampaignsThunk({ page: 1, limit: 10 }));
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const getCampaignLogThunk = createAsyncThunk(
  "ota/campaignLog",
  async (params, { rejectWithValue }) => {
    try {
      const campaignId =
        typeof params === "object" ? params.campaignId : params;
      const query = typeof params === "object" ? params : {};
      const response = await _get(otaCampaignLogUrl(campaignId), {
        page: query.page ?? 1,
        limit: query.limit ?? 100,
        search: query.search || undefined,
        status:
          query.statusFilter && query.statusFilter !== "all"
            ? query.statusFilter
            : undefined,
        eventsLimit: query.eventsLimit ?? 100,
      });
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const retryCampaignThunk = createAsyncThunk(
  "ota/retryCampaign",
  async (campaignId, { rejectWithValue }) => {
    try {
      const response = await _post(otaCampaignRetryUrl(campaignId), {});
      successToast(response?.data?.message || "Retry triggered");
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const getOtaSessionsThunk = createAsyncThunk(
  "ota/sessionList",
  async (params, { rejectWithValue }) => {
    try {
      const response = await _get(OTA_SESSION_LIST_URL, params);
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const triggerOtaThunk = createAsyncThunk(
  "ota/trigger",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await _post(OTA_TRIGGER_URL, payload);
      if (!payload.silent) {
        successToast(response?.data?.message || "OTA triggered");
        dispatch(getOtaSessionsThunk({ page: 1, limit: 50 }));
      }
      return response?.data?.result;
    } catch (error) {
      if (!payload.silent) errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

const BATCH_DELAY_MS = 300;

export const submitOtaBatchThunk = createAsyncThunk(
  "ota/submitBatch",
  async ({ file, version, targets, notes }, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("version", String(version));
      formData.append("targets", JSON.stringify(targets));
      if (notes) formData.append("notes", notes);
      const response = await _postFile(OTA_SUBMIT_URL, formData);
      successToast(response?.data?.message || "OTA update submitted");
      dispatch(getActiveCampaignThunk());
      dispatch(getOtaCampaignsThunk({ page: 1, limit: 10 }));
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const triggerOtaBatchThunk = createAsyncThunk(
  "ota/triggerBatch",
  async ({ targets, targetVersion }, { dispatch, rejectWithValue }) => {
    const results = [];
    const errors = [];

    for (let i = 0; i < targets.length; i += 1) {
      const target = targets[i];
      try {
        const response = await _post(OTA_TRIGGER_URL, {
          dbDeviceId: target.dbDeviceId,
          mqttDeviceId: target.mqttDeviceId,
          targetVersion,
        });
        if (response?.data?.result) results.push(response.data.result);
      } catch (error) {
        errors.push({ mqttDeviceId: target.mqttDeviceId, error });
      }
      if (i < targets.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS));
      }
    }

    dispatch(getOtaSessionsThunk({ page: 1, limit: 50 }));

    if (errors.length === 0) {
      successToast(`OTA triggered for ${results.length} equipment unit(s)`);
    } else if (results.length > 0) {
      successToast(
        `OTA triggered for ${results.length} unit(s); ${errors.length} failed`,
      );
    } else {
      errorHandler(errors[0]?.error);
      return rejectWithValue(errors);
    }

    return { results, errors };
  },
);

export const clearOtaRetainedThunk = createAsyncThunk(
  "ota/clearRetained",
  async (mqttDeviceId, { rejectWithValue, dispatch }) => {
    try {
      const response = await _post(otaClearRetainedUrl(mqttDeviceId), {});
      successToast(response?.data?.message || "Retained OTA cleared");
      dispatch(getOtaSessionsThunk({ page: 1, limit: 20 }));
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

export const resetOtaRetryThunk = createAsyncThunk(
  "ota/resetRetry",
  async (mqttDeviceId, { rejectWithValue }) => {
    try {
      const response = await _post(otaResetRetryUrl(mqttDeviceId), {});
      successToast(response?.data?.message || "OTA retry counter reset");
      return response?.data?.result;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error);
    }
  },
);

const MAX_LIVE_LOG_EVENTS = 100;

function patchDeviceByIndex(devices, deviceIndexByMqttId, payload) {
  if (!devices?.length) return null;
  const idx = deviceIndexByMqttId?.[payload.mqttDeviceId];
  if (idx == null || !devices[idx]) return null;
  const oldStatus = devices[idx].status;
  devices[idx] = applyLivePayloadToDevice(devices[idx], payload);
  return { row: devices[idx], oldStatus };
}

function syncCampaignRowCounts(state, campaignId, counts) {
  if (!campaignId) return;
  const idx = state.campaigns.findIndex((row) =>
    campaignIdsMatch(row.campaignId, campaignId),
  );
  if (idx === -1) return;
  state.campaigns[idx] = {
    ...state.campaigns[idx],
    successCount: counts.successCount,
    failureCount: counts.failureCount,
    pendingCount: counts.pendingCount,
    deviceCount: counts.deviceCount,
    status: counts.status,
  };
}

function applyLiveCountsUpdate(
  state,
  campaignId,
  oldStatus,
  newStatus,
  logRow,
  payload,
) {
  if (!campaignId) return;

  const isMessageOnly =
    Boolean(payload?.message) && !KNOWN_LIVE_STATUSES.has(newStatus);

  if (isMessageOnly) {
    if (
      state.campaignLog &&
      campaignIdsMatch(state.campaignLog.campaignId, campaignId)
    ) {
      state.campaignLog = {
        ...state.campaignLog,
        events: [
          buildLiveLogEvent(
            logRow ?? { mqttDeviceId: payload.mqttDeviceId },
            payload,
          ),
          ...(state.campaignLog.events ?? []),
        ].slice(0, MAX_LIVE_LOG_EVENTS),
      };
    }
    return;
  }

  if (!oldStatus || !newStatus || oldStatus === newStatus) {
    return;
  }

  const baseCounts =
    state.campaignLog &&
    campaignIdsMatch(state.campaignLog.campaignId, campaignId)
      ? state.campaignLog
      : state.activeCampaign &&
          campaignIdsMatch(state.activeCampaign.campaignId, campaignId)
        ? state.activeCampaign
        : null;

  if (!baseCounts) return;

  const counts = adjustCampaignCounts(baseCounts, oldStatus, newStatus);

  if (
    state.campaignLog &&
    campaignIdsMatch(state.campaignLog.campaignId, campaignId)
  ) {
    const nextEvents = shouldAppendLiveEvent(oldStatus, newStatus, payload)
      ? [
          buildLiveLogEvent(
            logRow ?? { mqttDeviceId: payload.mqttDeviceId },
            payload,
          ),
          ...(state.campaignLog.events ?? []),
        ].slice(0, MAX_LIVE_LOG_EVENTS)
      : state.campaignLog.events;

    state.campaignLog = {
      ...state.campaignLog,
      ...counts,
      events: nextEvents,
    };
  }

  syncCampaignRowCounts(state, campaignId, counts);

  if (
    state.activeCampaign &&
    campaignIdsMatch(state.activeCampaign.campaignId, campaignId)
  ) {
    if (counts.pendingCount === 0) {
      state.activeCampaign = null;
    } else {
      state.activeCampaign = { ...state.activeCampaign, ...counts };
    }
  }
}

const otaSlice = createSlice({
  name: "otaSlice",
  initialState: INITIAL_STATE,
  reducers: {
    updateOtaLive(state, action) {
      const payload = action.payload;
      if (!payload?.mqttDeviceId) return;

      const prevLive = state.liveByMqttId[payload.mqttDeviceId];
      const newStatus = payload.status;
      state.liveByMqttId[payload.mqttDeviceId] = payload;

      const sessionIdx = state.sessions.findIndex(
        (row) => row.mqttDeviceId === payload.mqttDeviceId,
      );
      if (sessionIdx !== -1) {
        state.sessions[sessionIdx] = applyLivePayloadToDevice(
          state.sessions[sessionIdx],
          payload,
        );
      }

      const campaignId =
        state.activeCampaign?.campaignId ?? state.campaignLog?.campaignId;

      const patched = patchDeviceByIndex(
        state.campaignLog?.devices,
        state.campaignLog?.deviceIndexByMqttId,
        payload,
      );

      const oldStatus = patched?.oldStatus ?? prevLive?.status;
      if (patched?.row) {
        // Device row on current page updated in place.
      }

      applyLiveCountsUpdate(
        state,
        campaignId,
        oldStatus,
        newStatus,
        patched?.row,
        payload,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFirmwareListThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFirmwareListThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.firmwareList = action.payload ?? [];
      })
      .addCase(getFirmwareListThunk.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(uploadFirmwareThunk.pending, (state) => {
        state.isUploading = true;
      })
      .addCase(uploadFirmwareThunk.fulfilled, (state) => {
        state.isUploading = false;
      })
      .addCase(uploadFirmwareThunk.rejected, (state) => {
        state.isUploading = false;
      })
      .addCase(getOtaSessionsThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getOtaSessionsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessions = action.payload?.rows ?? [];
        state.sessionsTotal = action.payload?.total ?? 0;
      })
      .addCase(getOtaSessionsThunk.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getOtaCampaignsThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getOtaCampaignsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.campaigns = action.payload?.rows ?? [];
        state.campaignsTotal = action.payload?.total ?? 0;
      })
      .addCase(getOtaCampaignsThunk.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getActiveCampaignThunk.fulfilled, (state, action) => {
        state.activeCampaign = action.payload ?? null;
      })
      .addCase(getCampaignLogThunk.pending, (state, action) => {
        if (action.meta.arg?.append) {
          state.isLoadingMoreLog = true;
        } else {
          state.isLoadingLog = true;
        }
      })
      .addCase(getCampaignLogThunk.fulfilled, (state, action) => {
        state.isLoadingLog = false;
        state.isLoadingMoreLog = false;
        const payload = action.payload ?? null;
        const append = action.meta.arg?.append === true;

        if (!payload) {
          state.campaignLog = null;
          return;
        }

        if (
          append &&
          state.campaignLog &&
          campaignIdsMatch(state.campaignLog.campaignId, payload.campaignId)
        ) {
          const existingIds = new Set(
            (state.campaignLog.devices ?? []).map((row) => row.mqttDeviceId),
          );
          const newDevices = (payload.devices ?? []).filter(
            (row) => !existingIds.has(row.mqttDeviceId),
          );
          const mergedDevices = [
            ...(state.campaignLog.devices ?? []),
            ...newDevices,
          ];
          state.campaignLog = {
            ...payload,
            events: state.campaignLog.events ?? payload.events,
            devices: mergedDevices,
            deviceIndexByMqttId: buildDeviceIndexMap(mergedDevices),
          };
          return;
        }

        state.campaignLog = {
          ...payload,
          deviceIndexByMqttId: buildDeviceIndexMap(payload.devices ?? []),
        };
      })
      .addCase(getCampaignLogThunk.rejected, (state) => {
        state.isLoadingLog = false;
        state.isLoadingMoreLog = false;
      })
      .addCase(deployOtaBatchThunk.pending, (state) => {
        state.isTriggering = true;
      })
      .addCase(deployOtaBatchThunk.fulfilled, (state) => {
        state.isTriggering = false;
      })
      .addCase(deployOtaBatchThunk.rejected, (state) => {
        state.isTriggering = false;
      })
      .addCase(retryCampaignThunk.pending, (state) => {
        state.isRetrying = true;
      })
      .addCase(retryCampaignThunk.fulfilled, (state) => {
        state.isRetrying = false;
      })
      .addCase(retryCampaignThunk.rejected, (state) => {
        state.isRetrying = false;
      })
      .addCase(getOtaEquipmentTargetsThunk.pending, (state) => {
        state.isSelectingAll = true;
      })
      .addCase(getOtaEquipmentTargetsThunk.fulfilled, (state) => {
        state.isSelectingAll = false;
      })
      .addCase(getOtaEquipmentTargetsThunk.rejected, (state) => {
        state.isSelectingAll = false;
      })
      .addCase(triggerOtaThunk.pending, (state) => {
        state.isTriggering = true;
      })
      .addCase(triggerOtaThunk.fulfilled, (state, action) => {
        state.isTriggering = false;
        if (action.payload?.id) {
          state.sessions = [action.payload, ...state.sessions.filter((s) => s.id !== action.payload.id)];
        }
      })
      .addCase(triggerOtaThunk.rejected, (state) => {
        state.isTriggering = false;
      })
      .addCase(submitOtaBatchThunk.pending, (state) => {
        state.isTriggering = true;
      })
      .addCase(submitOtaBatchThunk.fulfilled, (state, action) => {
        state.isTriggering = false;
        const created = action.payload?.sessions ?? [];
        if (created.length) {
          const ids = new Set(created.map((row) => row.id));
          state.sessions = [
            ...created,
            ...state.sessions.filter((row) => !ids.has(row.id)),
          ];
        }
      })
      .addCase(submitOtaBatchThunk.rejected, (state) => {
        state.isTriggering = false;
      })
      .addCase(triggerOtaBatchThunk.pending, (state) => {
        state.isTriggering = true;
      })
      .addCase(triggerOtaBatchThunk.fulfilled, (state, action) => {
        state.isTriggering = false;
        const created = action.payload?.results ?? [];
        if (created.length) {
          const ids = new Set(created.map((row) => row.id));
          state.sessions = [
            ...created,
            ...state.sessions.filter((row) => !ids.has(row.id)),
          ];
        }
      })
      .addCase(triggerOtaBatchThunk.rejected, (state) => {
        state.isTriggering = false;
      });
  },
});

export const { updateOtaLive } = otaSlice.actions;
export const isOtaTerminal = (status) => TERMINAL_STATUSES.has(status);
export default otaSlice.reducer;
